<script setup lang="ts">
import { ref, computed, onMounted } from 'vue';
import { Plus } from 'lucide-vue-next';
// shadcn-vue 컴포넌트 임포트
import { Input } from '@/components/shadcnui/input';
import { Label } from '@/components/shadcnui/label';
import { Button } from '@/components/shadcnui/button';
import { Skeleton } from '@/components/shadcnui/skeleton';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/shadcnui/tooltip';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/shadcnui/table';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/shadcnui/dialog';
import { Pagination, PaginationContent, PaginationEllipsis, PaginationItem, PaginationPrevious, PaginationNext } from '@/components/shadcnui/pagination';
// composable 및 store 임포트
import { useRSStore } from '@/store/useRSStore';
import { useUserStore } from '@/store/useUserStore';
import { useChipset } from '@/composable/useChipset';
// 타입 임포트
import type { chipset, addchipset } from '@/types/types';

// 스토어 및 composable 초기화
const RSStore = useRSStore();
const UserStore = useUserStore();
const { onReadChipsetList, onAddChipset, onUpdateChipset, onDeleteChipset, onPaginationChipset } = useChipset();

// 상태 관리
const isDialogOpen = ref(false);
const isUpdateMode = ref(false);
const currentPage = ref(1);
const totalpage = ref(1);
const isLoading = ref(true);
const formData = ref<chipset>({ id: 0, name: "", code: "" });
const isFormInvalid = computed(() => {
  return !formData.value.name?.trim() || !formData.value.code?.trim();
});

// 폼 데이터 초기화 함수
const formDataReset = () => {
  formData.value = { id: 0, name: "", code: "" };
};

// 다이얼로그 핸들러
const openAddDialog = () => {
  isUpdateMode.value = false;
  formDataReset();
  isDialogOpen.value = true;
};
const openUpdateDialog = (chipset: chipset) => {
  if (!UserStore.chipsetEdit && !UserStore.chipsetDelete) {
    return;
  }
  isUpdateMode.value = true;
  formData.value = { ...chipset };
  isDialogOpen.value = true;
};
const cancelDialog = () => {
  isDialogOpen.value = false;
  formDataReset();
};

// ADD/UPDATE/DELETE API 호출 핸들러 (API 호출 후 데이터 갱신)
const handleAdd = async () => {
  isLoading.value = true;
  UserStore.chipsetPage.page = 1;
  const addchipset: addchipset = {
    name: formData.value.name,
    code: formData.value.code,
  };
  await onAddChipset(addchipset);
  await onReadChipsetList(UserStore.chipsetPage).finally(() => {
    isLoading.value = false;
  });
  isDialogOpen.value = false;
};
const handleUpdate = async () => {
  isLoading.value = true;
  const updatechipset: chipset = {
    id: formData.value.id,
    name: formData.value.name,
    code: formData.value.code,
  };
  await onUpdateChipset(updatechipset);
  await onReadChipsetList(UserStore.chipsetPage).finally(() => {
    isLoading.value = false;
  });
  isDialogOpen.value = false;
};
const handleDelete = async () => {
  isLoading.value = true;
  await onDeleteChipset(formData.value.id);
  await onReadChipsetList(UserStore.chipsetPage).finally(() => {
    isLoading.value = false;
  });
  isDialogOpen.value = false;
};

const paginationHandler = async (page: number) => {
  if (page < 1) {
    page = 1;
  }
  else if (page > totalpage.value) {
    page = totalpage.value;
  }
  currentPage.value = page;
  UserStore.chipsetPage.page = currentPage.value;
  isLoading.value = true;
  await onReadChipsetList(UserStore.chipsetPage).finally(() => {
    isLoading.value = false;
  });
};

// 컴포넌트 마운트 시 데이터 로드
onMounted(async () => {
  totalpage.value = await onPaginationChipset(UserStore.itemPerPage) ?? 1;
 if (UserStore.chipsetPage.page) {
    currentPage.value = UserStore.chipsetPage.page;
  }
  await onReadChipsetList(UserStore.chipsetPage).finally(() => {
    isLoading.value = false;
  });
});
</script>

<template>
  <div class="p-8">
    <!-- 헤더 -->
    <div class="flex items-center justify-between mb-6">
      <h1 class="text-3xl font-bold text-foreground">칩셋</h1>
      <Button @click="openAddDialog" :disabled="!UserStore.chipsetAdd">
        <Plus class="mr-2 h-4 w-4" />
        Add
      </Button>
    </div>
    <!-- 데이터 테이블 -->
    <div class="border rounded-lg bg-card overflow-hidden">
      <Table>
        <TableHeader>
          <TableRow class="bg-muted/50 hover:bg-muted/50">
            <TableHead class="font-semibold px-5">ID</TableHead>
            <TableHead class="font-semibold">Name</TableHead>
            <TableHead class="font-semibold">Code</TableHead>
          <!--  <TableHead class="font-semibold w-[500px]">Remark</TableHead> -->
          </TableRow>
        </TableHeader>
        <TableBody>
          <!-- 로딩 상태  -->
          <template v-if="isLoading">
            <TableRow v-for="i in UserStore.itemPerPage" :key="i">
              <TableCell colspan="5" class="px-3">
                <Skeleton class="h-5" />
              </TableCell>
            </TableRow>
          </template>
          <!-- 데이터 목록 -->
          <template v-else>
            <TableRow v-for="chipset in RSStore.chipsetList" :key="chipset.id" class="cursor-pointer hover:bg-accent/50"
              @click="openUpdateDialog(chipset)" >
              <TableCell class="px-5">{{ chipset.id }}</TableCell>
              <TableCell class="font-medium text-foreground">{{ chipset.name }}</TableCell>
              <TableCell class="px-5">{{ chipset.code }}</TableCell>
              <!--  <TableCell class="text-muted-foreground">{{ chipset.remark }}</TableCell> -->
            </TableRow>
            <TableRow v-for="i in (UserStore.itemPerPage - RSStore.chipsetList.length)" :key="'empty-' + i">
              <TableCell colspan="4">&nbsp;</TableCell>
            </TableRow>
          </template>
        </TableBody>
      </Table>
    </div>
    <!-- 페이지네이션 -->
    <div class="mt-4 flex justify-center">
      <Pagination v-model:page="currentPage" :total="totalpage * UserStore.itemPerPage"
        :items-per-page="UserStore.itemPerPage" :sibling-count="UserStore.siblingCount" show-edges>
        <PaginationContent v-slot="{ items }" class="flex items-center gap-1">
          <PaginationPrevious @click="paginationHandler(currentPage - RSStore.nextunit)" />
          <template v-for="(item, index) in items">
            <PaginationItem v-if="item.type === 'page'" :key="index" :value="item.value" as-child>
              <Button class="w-10 h-10 p-0" :variant="item.value === currentPage ? 'default' : 'outline'"
                @click="paginationHandler(item.value)">
                {{ item.value }}
              </Button>
            </PaginationItem>
            <PaginationEllipsis v-else :key="item.type" :index="index" />
          </template>
          <PaginationNext @click="paginationHandler(currentPage + RSStore.nextunit)" />
        </PaginationContent>
      </Pagination>
    </div>
    <!-- 추가/수정 다이얼로그 -->
    <Dialog v-model:open="isDialogOpen">
      <DialogContent class="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle>
            {{ isUpdateMode ? "칩셋 수정" : "칩셋 추가" }}
          </DialogTitle>
          <DialogDescription>
            {{ isUpdateMode ? "칩셋 정보를 수정하세요" : "새로운 칩셋 정보를 입력하세요" }}
          </DialogDescription>
        </DialogHeader>
        <div class="grid gap-4 py-4">
          <div class="grid gap-2">
            <Label for="name">Name</Label>
            <Input id="name" v-model="formData.name" placeholder="칩셋 이름 입력" required maxlength="32" />
          </div>
          <div class="grid gap-2">
            <Label for="code">Code</Label>
            <Input id="code" v-model="formData.code" placeholder="칩셋 코드 입력" required maxlength="32" />
          </div>
          <div class="grid gap-2">
            <Label for="remark">Remark</Label>
            <Input id="remark" placeholder="비고 입력" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" @click="cancelDialog">Cancel</Button>
          <Button v-if="isUpdateMode" variant="destructive" @click="handleDelete" :disabled="!UserStore.chipsetDelete">
            Delete
          </Button>
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger as-child>
                <div class="inline-block cursor-not-allowed">
                  <Button @click="isUpdateMode ? handleUpdate() : handleAdd()" :disabled="isFormInvalid || (!UserStore.chipsetEdit && isUpdateMode)"
                    :class="{ 'pointer-events-none': isFormInvalid }">
                    {{ isUpdateMode ? "Update" : "Add" }}
                  </Button>
                </div>
              </TooltipTrigger>
              <TooltipContent>
                <p v-if="isFormInvalid">name, code를 입력해주세요.</p>
                <p v-else>칩셋 정보를 저장합니다.</p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  </div>
</template>